import { Editor } from "@tiptap/vue-2";
import { getEmailTiptapExtensions } from "./tiptapExtensions";

function px(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? `${n}px` : String(v);
}

function safeUrl(url) {
  if (!url) return "";
  const u = String(url).trim();
  if (!u) return "";
  if (u.startsWith("#")) return u;
  if (/^(https?:|mailto:|tel:)/i.test(u)) return u;
  return u;
}

function div(attrs, inner) {
  return `<div${attrs ? " " + attrs : ""}>${inner || ""}</div>`;
}

function formatBorderRadius(value) {
  if (!value) return "0px";
  // If it's an object with individual corner values
  if (typeof value === "object" && !Array.isArray(value)) {
    const topLeft = Number(value.topLeft) || 0;
    const topRight = Number(value.topRight) || 0;
    const bottomLeft = Number(value.bottomLeft) || 0;
    const bottomRight = Number(value.bottomRight) || 0;
    return `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;
  }
  // If it's a number, use uniform radius
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? `${n}px` : "0px";
}

function getColumnTdBorderRadius(borderRadius, colIdx, colCount) {
  if (!borderRadius) return {};
  const isFirst = colIdx === 0;
  const isLast = colIdx === colCount - 1;
  const isOnly = colCount === 1;
  
  // If it's an object with individual corner values
  if (typeof borderRadius === "object" && !Array.isArray(borderRadius)) {
    const topLeft = Number(borderRadius.topLeft) || 0;
    const topRight = Number(borderRadius.topRight) || 0;
    const bottomLeft = Number(borderRadius.bottomLeft) || 0;
    const bottomRight = Number(borderRadius.bottomRight) || 0;
    
    if (isOnly) {
      return {
        "border-top-left-radius": `${topLeft}px`,
        "border-top-right-radius": `${topRight}px`,
        "border-bottom-right-radius": `${bottomRight}px`,
        "border-bottom-left-radius": `${bottomLeft}px`,
      };
    } else if (isFirst) {
      return {
        "border-top-left-radius": `${topLeft}px`,
        "border-bottom-left-radius": `${bottomLeft}px`,
      };
    } else if (isLast) {
      return {
        "border-top-right-radius": `${topRight}px`,
        "border-bottom-right-radius": `${bottomRight}px`,
      };
    }
    return {};
  }
  
  // If it's a number, use uniform radius
  const n = Number(borderRadius);
  if (!Number.isFinite(n) || n < 0) return {};
  const radius = `${n}px`;
  
  if (isOnly) {
    return {
      "border-radius": radius,
    };
  } else if (isFirst) {
    return {
      "border-top-left-radius": radius,
      "border-bottom-left-radius": radius,
    };
  } else if (isLast) {
    return {
      "border-top-right-radius": radius,
      "border-bottom-right-radius": radius,
    };
  }
  return {};
}

function getColumnTdBorderRadiusForMobile(borderRadius, isFirstRow, isLastRow, isOnlyRow) {
  if (!borderRadius) return {};
  
  // If it's an object with individual corner values
  if (typeof borderRadius === "object" && !Array.isArray(borderRadius)) {
    const topLeft = Number(borderRadius.topLeft) || 0;
    const topRight = Number(borderRadius.topRight) || 0;
    const bottomLeft = Number(borderRadius.bottomLeft) || 0;
    const bottomRight = Number(borderRadius.bottomRight) || 0;
    
    if (isOnlyRow) {
      return {
        "border-top-left-radius": `${topLeft}px`,
        "border-top-right-radius": `${topRight}px`,
        "border-bottom-right-radius": `${bottomRight}px`,
        "border-bottom-left-radius": `${bottomLeft}px`,
      };
    } else if (isFirstRow) {
      return {
        "border-top-left-radius": `${topLeft}px`,
        "border-top-right-radius": `${topRight}px`,
      };
    } else if (isLastRow) {
      return {
        "border-bottom-right-radius": `${bottomRight}px`,
        "border-bottom-left-radius": `${bottomLeft}px`,
      };
    }
    return {};
  }
  
  // If it's a number, use uniform radius
  const n = Number(borderRadius);
  if (!Number.isFinite(n) || n < 0) return {};
  const radius = `${n}px`;
  
  if (isOnlyRow) {
    return {
      "border-radius": radius,
    };
  } else if (isFirstRow) {
    return {
      "border-top-left-radius": radius,
      "border-top-right-radius": radius,
    };
  } else if (isLastRow) {
    return {
      "border-bottom-right-radius": radius,
      "border-bottom-left-radius": radius,
    };
  }
  return {};
}

function styleObjToString(styleObj) {
  if (!styleObj) return "";
  return Object.entries(styleObj)
    .filter(([, v]) => v !== null && v !== undefined && v !== "")
    .map(([k, v]) => `${k}:${v}`)
    .join(";");
}

function escapeHtmlAttr(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

function backgroundImageUrlFromSettings(s = {}) {
  const mode = String(s.backgroundMode || "").toLowerCase() === "image" ? "image" : "color";
  const img = typeof s.backgroundImageUrl === "string" ? s.backgroundImageUrl.trim() : "";
  return mode === "image" && img ? img : null;
}

// Gmail (Chrome webmail) can strip the entire inline style attribute when it
// sees background-image:url(...) or background:url(...). Put the image on the
// HTML background="" attribute instead, and keep size/position/repeat in CSS.
// https://github.com/hteumeuleu/caniemail/issues/343
function htmlBackgroundAttr(url) {
  if (!url) return "";
  return ` background="${escapeHtmlAttr(url)}"`;
}

function backgroundStylesFromSettings(s = {}) {
  const img = backgroundImageUrlFromSettings(s);
  if (!img) {
    return {
      "background-image": null,
      "background-size": null,
      "background-position": null,
      "background-repeat": null,
    };
  }

  const sizeModeRaw = String(s.backgroundSizeMode || "").toLowerCase();
  const sizeMode = sizeModeRaw === "contain" || sizeModeRaw === "custom" ? sizeModeRaw : "cover";
  const sizeUnitRaw = String(s.backgroundSizeUnit || "").toLowerCase();
  const sizeUnit = sizeUnitRaw === "px" ? "px" : "pct";

  const pxVal = Number(s.backgroundSizePx);
  const sizePx = Number.isFinite(pxVal) ? Math.min(2000, Math.max(100, Math.round(pxVal))) : 600;
  const pctVal = Number(s.backgroundSizePct);
  const sizePct = Number.isFinite(pctVal) ? Math.min(100, Math.max(0, Math.round(pctVal))) : 100;

  const repeatRaw = String(s.backgroundRepeat || "").toLowerCase();
  const repeat =
    repeatRaw === "repeat" || repeatRaw === "repeat-x" || repeatRaw === "repeat-y"
      ? repeatRaw
      : "no-repeat";

  const position = String(s.backgroundPosition || "center center");

  const backgroundSize =
    sizeMode === "cover" || sizeMode === "contain"
      ? sizeMode
      : (sizeUnit === "px" ? `${sizePx}px auto` : `${sizePct}% auto`);

  return {
    "background-size": backgroundSize,
    "background-position": position,
    "background-repeat": repeat,
  };
}

function normalizeAlign(raw) {
  const v = String(raw || "").toLowerCase();
  return v === "left" || v === "center" || v === "right" ? v : null;
}

function normalizeVAlign(raw) {
  const v = String(raw || "").toLowerCase();
  return v === "middle" || v === "bottom" ? v : "top";
}

function hexToCssColor(hex) {
  if (!hex || typeof hex !== "string") return hex;
  const h = hex.trim();
  // If 8-digit hex (#RRGGBBAA), convert to rgba()
  if (h.length === 9 && h.startsWith("#")) {
    const r = parseInt(h.substring(1, 3), 16);
    const g = parseInt(h.substring(3, 5), 16);
    const b = parseInt(h.substring(5, 7), 16);
    const a = parseInt(h.substring(7, 9), 16) / 255;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  // Otherwise return as-is (6-digit hex or other format)
  return hex;
}

function borderToSideStyles(border, { width = 1, defaultColor = "#e5e7eb" } = {}) {
  const b = border || {};
  if (!b.enabled) return {};
  const styleRaw = String(b.style || "solid").toLowerCase();
  const style = styleRaw === "dashed" || styleRaw === "dotted" ? styleRaw : "solid";
  const colorRaw = typeof b.color === "string" && b.color ? b.color : defaultColor;
  const color = hexToCssColor(colorRaw);
  const sides = b.sides || {};
  const isOn = (k) => sides[k] !== false;
  const css = (on) => (on ? `${width}px ${style} ${color}` : null);
  return {
    "border-top": css(isOn("top")),
    "border-right": css(isOn("right")),
    "border-bottom": css(isOn("bottom")),
    "border-left": css(isOn("left")),
  };
}

function outlineToStyles(border, { defaultColor = "#e5e7eb" } = {}) {
  const b = border || {};
  if (!b.enabled) return {};
  const styleRaw = String(b.style || "solid").toLowerCase();
  const style = styleRaw === "dashed" || styleRaw === "dotted" ? styleRaw : "solid";
  const colorRaw = typeof b.color === "string" && b.color ? b.color : defaultColor;
  const color = hexToCssColor(colorRaw);
  const wRaw = Number(b.width ?? 1);
  const width = Number.isFinite(wRaw) ? Math.min(20, Math.max(1, Math.round(wRaw))) : 1;
  const css = `${width}px ${style} ${color}`;
  return {
    outline: css,
    "outline-offset": "0px",
  };
}

function preserveEmailBlankLines(html) {
  if (!html) return html;
  // TipTap empty paragraphs become <p></p> or <p><br></p>. Email CSS zeros p
  // margin, so those collapse to 0 height. &nbsp; keeps one line like the editor.
  return String(html).replace(
    /<p(\b[^>]*)>(?:\s|&nbsp;|<br\b[^>]*>)*<\/p>/gi,
    "<p$1>&nbsp;</p>",
  );
}

function tiptapJsonToHtml(json) {
  try {
    // Avoid `@tiptap/html` (brings modern-syntax deps that laravel-mix v4 may not transpile).
    // We reuse a single offscreen editor instance to convert JSON -> HTML.
    if (!tiptapJsonToHtml.__editor) {
      tiptapJsonToHtml.__editor = new Editor({
        editable: false,
        extensions: getEmailTiptapExtensions({ withPlaceholder: false }),
        content: json,
      });
    } else {
      tiptapJsonToHtml.__editor.commands.setContent(json, false);
    }
    return preserveEmailBlankLines(tiptapJsonToHtml.__editor.getHTML());
  } catch (e) {
    console.error(e);
    return "";
  }
}

function renderTextBlock(block, doc) {
  const s = block.settings || {};
  const align = normalizeAlign(s.align);
  const displayMode = s.display === "inline-block" || s.display === "inline" ? "inline-block" : "block";
  const json = s.content?.tiptapJson || { type: "doc", content: [{ type: "paragraph" }] };
  const html = tiptapJsonToHtml(json);

  const style = {
    "font-family": doc.body?.fontFamily,
    color: doc.body?.textColor,
    display: displayMode,
  };
  // Only set text-align if block has explicit alignment (allows inheritance from column)
  if (align) {
    style["text-align"] = align;
  }

  // Width support for inline-block text elements
  if (displayMode === "inline-block") {
    const widthModeRaw = String(s.widthMode || "").toLowerCase();
    const widthMode = widthModeRaw === "px" || widthModeRaw === "pct" ? widthModeRaw : "pct";
    const widthPx = Number(s.widthPx);
    const w = Number.isFinite(widthPx) && widthPx > 0 ? `${Math.round(widthPx)}px` : null;
    const widthPctRaw = Number(s.widthPct);
    const widthPct = Number.isFinite(widthPctRaw) ? Math.min(100, Math.max(10, Math.round(widthPctRaw))) : 100;
    const wp = `${widthPct}%`;

    if (widthMode === "px" && w) {
      style.width = w;
      style["max-width"] = w;
    } else if (widthMode === "pct") {
      style.width = wp;
      style["max-width"] = "100%";
    }
  }

  return div(`style="${styleObjToString(style)}"`, html);
}

function renderImageInner(block, { mode, inline = false } = {}) {
  const s = block.settings || {};
  const src = s.src || "";
  const alt = (s.alt || "").replace(/"/g, "&quot;");
  const href = safeUrl(s.href);
  const align = normalizeAlign(s.align);
  const displayMode = s.display === "inline-block" || s.display === "inline" ? "inline-block" : "block";
  const widthPx = Number(s.widthPx);
  const w = Number.isFinite(widthPx) && widthPx > 0 ? `${Math.round(widthPx)}px` : null;
  const borderRadius = s.borderRadius ?? 0;
  const br = formatBorderRadius(borderRadius);
  const borderW = Number(s.borderWidth ?? 0);
  const borderWidth = Number.isFinite(borderW) && borderW > 0 ? Math.round(borderW) : 0;
  const borderColorRaw = s.borderColor || "#e5e7eb";
  const borderColor = hexToCssColor(borderColorRaw);
  const border = borderWidth ? `${borderWidth}px solid ${borderColor}` : null;
  const objectFit = "cover";
  const aspectRatio = s.aspectRatio || "auto";

  if (!src && mode !== "preview") return "";

  if (!src && mode === "preview") {
    // Inline social/icon images should show a small square/circle swatch placeholder (no text),
    // otherwise it breaks layouts like the "social icons row".
    const wantsInline = inline || displayMode === "inline-block";
    if (wantsInline && w) {
      return `<div style="${styleObjToString({
        display: "inline-block",
        width: w,
        height: w,
        "border-radius": br,
        border: border || "1px solid #d0d5dd",
        background: "#f9fafb",
        "box-sizing": "border-box",
      })}"></div>`;
    }

    const baseStyle = {
      border: "1px dashed #d0d5dd",
      "border-radius": br,
      background: "#f9fafb",
      display: "flex",
      "align-items": "center",
      "justify-content": "center",
      color: "#667085",
      "font-size": "13px",
      width: inline || displayMode === "inline" ? (w || "100%") : "100%",
      "max-width": inline || displayMode === "inline" ? (w || null) : (w || null),
      "box-sizing": "border-box",
    };
    const placeholderAr = aspectRatio && aspectRatio !== "auto" ? aspectRatio : "16 / 9";
    if (placeholderAr) {
      return `<div style="${styleObjToString({
        ...baseStyle,
        "aspect-ratio": placeholderAr,
        overflow: "hidden",
        padding: "10px",
      })}">Sem imagem (configure no painel)</div>`;
    }
    return `<div style="${styleObjToString({
      ...baseStyle,
      height: "140px",
    })}">Sem imagem (configure no painel)</div>`;
  }

  const imgInlineStyleBase = {
    // When align is unset, prefer inline-block so parent text-align (column) can control placement.
    display: inline || displayMode === "inline" || (!align && displayMode === "block") ? "inline-block" : "block",
    border: "0",
    outline: "none",
    "text-decoration": "none",
    width: inline || displayMode === "inline" ? (w || "auto") : "100%",
    "max-width": inline || displayMode === "inline" ? (w || "100%") : (w || "100%"),
    margin: align === "center" ? "0 auto" : (align === "right" ? "0 0 0 auto" : null),
    "box-sizing": "border-box",
  };

  let imgHtml = "";
  if (aspectRatio && aspectRatio !== "auto") {
    // Modern clients: honor aspect-ratio + object-fit.
    // Older email clients will ignore aspect-ratio and fallback to natural image height.
    imgHtml = `<div style="${styleObjToString({
      width: inline || displayMode === "inline" ? (w || "100%") : "100%",
      "max-width": inline || displayMode === "inline" ? (w || null) : (w || null),
      overflow: "hidden",
      "border-radius": br,
      border,
      "box-sizing": "border-box",
      "aspect-ratio": aspectRatio,
      "line-height": "0",
      "font-size": "0",
    })}"><img src="${src}" alt="${alt}" style="${styleObjToString({
      ...imgInlineStyleBase,
      height: "100%",
      "object-fit": objectFit,
      "border-radius": "0",
    })}" /></div>`;
  } else {
    imgHtml = `<img src="${src}" alt="${alt}" style="${styleObjToString({
      ...imgInlineStyleBase,
      height: "auto",
      "object-fit": objectFit,
      border,
      "border-radius": br,
    })}" />`;
  }

  const linkStyle =
    inline || displayMode === "inline"
      ? "display:inline-block"
      : (w ? `display:block;max-width:${w};margin:${align === "center" ? "0 auto" : align === "right" ? "0 0 0 auto" : "0 auto 0 0"}` : "display:block");
  return href ? `<a href="${href}" target="_blank" style="${linkStyle}">${imgHtml}</a>` : imgHtml;
}

function renderImageBlock(block, { mode, inline = false } = {}) {
  const s = block.settings || {};
  const align = normalizeAlign(s.align);
  const displayMode = s.display === "inline-block" || s.display === "inline" ? "inline-block" : "block";
  const widthModeRaw = String(s.widthMode || "").toLowerCase();
  const widthMode = widthModeRaw === "px" || widthModeRaw === "pct" ? widthModeRaw : (displayMode === "inline-block" ? "px" : "pct");
  const widthPx = Number(s.widthPx);
  const w = Number.isFinite(widthPx) && widthPx > 0 ? `${Math.round(widthPx)}px` : null;
  const widthPctRaw = Number(s.widthPct);
  const widthPct = Number.isFinite(widthPctRaw) ? Math.min(100, Math.max(10, Math.round(widthPctRaw))) : 100;
  const wp = widthPct < 100 ? `${widthPct}%` : null;

  if (inline) return renderImageInner(block, { mode, inline: true });

  // Block-level output
  const inner = renderImageInner(block, { mode, inline: false });
  const hasConstraint = widthMode === "px" ? Boolean(w) : Boolean(wp);
  if (hasConstraint) {
    const margin = align
      ? (align === "center" ? "0 auto" : align === "right" ? "0 0 0 auto" : "0 auto 0 0")
      : null;
    const widthStyle =
      widthMode === "px"
        ? { width: "100%", "max-width": w, margin }
        : { width: wp, "max-width": "100%", margin };
    const outer = align ? { "text-align": align } : null;
    return `<div${outer ? ` style="${styleObjToString({ ...outer, "line-height": "0", "font-size": "0" })}"` : ` style="${styleObjToString({ "line-height": "0", "font-size": "0" })}"`}><div style="${styleObjToString({
      display: "inline-block",
      "line-height": "0",
      "font-size": "0",
      ...widthStyle,
    })}">${inner}</div></div>`;
  }
  const wrapStyle = styleObjToString({
    ...(align ? { "text-align": align } : {}),
    "line-height": "0",
    "font-size": "0",
  });
  return div(`style="${wrapStyle}"`, inner);
}

function renderButtonBlock(block, doc) {
  const s = block.settings || {};
  const href = safeUrl(s.href);
  const alignRaw = String(s.align || "").toLowerCase();
  const align = alignRaw === "fill" ? "fill" : normalizeAlign(alignRaw);
  // Back-compat: older docs may have stored width as align="fill"
  const isFill = s.width === "filled" || align === "fill";
  const textAlign = align === "fill" ? "left" : align;
  const json = s.content?.tiptapJson || null;
  const textFallback = (s.text || "Button").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const labelHtml = json ? tiptapJsonToHtml(json) : textFallback;

  const paddingX = Number(s.paddingX ?? 18);
  const paddingY = Number(s.paddingY ?? 12);
  const borderRadius = s.borderRadius ?? 8;
  const borderRadiusCss = formatBorderRadius(borderRadius);

  const btnBorder = s.border || null;
  const btnBorderWidthRaw = Number(btnBorder?.width ?? 1);
  const btnBorderWidth = Number.isFinite(btnBorderWidthRaw)
    ? Math.min(20, Math.max(1, Math.round(btnBorderWidthRaw)))
    : 1;

  const btnBgRaw = s.backgroundColor || doc.body?.buttonBackgroundColor || "#111827";
  const btnBg = hexToCssColor(btnBgRaw);
  const a = `<a href="${href}" target="_blank" style="${styleObjToString({
    display: isFill ? "block" : "inline-block",
    width: isFill ? "100%" : null,
    "box-sizing": isFill ? "border-box" : null,
    "text-align": textAlign,
    "background-color": btnBg,
    color: s.color || doc.body?.buttonTextColor || "#ffffff",
    "text-decoration": "none",
    "font-family": doc.body?.fontFamily,
    "font-size": "14px",
    "font-weight": "600",
    "border-radius": borderRadiusCss,
    padding: `${paddingY}px ${paddingX}px`,
    ...borderToSideStyles(btnBorder, { width: btnBorderWidth, defaultColor: "#e5e7eb" }),
  })}">${labelHtml}</a>`;

  const outerAlign = isFill ? "left" : textAlign;
  return outerAlign ? div(`style="text-align:${outerAlign}"`, a) : div("", a);
}

function renderDividerBlock(block) {
  const s = block.settings || {};
  const thickness = Number(s.thickness ?? 1);
  const colorRaw = s.color || "#e5e7eb";
  const color = hexToCssColor(colorRaw);
  const widthModeRaw = String(s.widthMode || "").toLowerCase();
  const widthMode = widthModeRaw === "px" ? "px" : "pct";
  const widthPx = Number(s.widthPx);
  const wpx = Number.isFinite(widthPx) && widthPx > 0 ? `${Math.round(widthPx)}px` : null;
  const widthPctRaw = Number(s.widthPct);
  const widthPct = Number.isFinite(widthPctRaw) ? Math.min(100, Math.max(10, Math.round(widthPctRaw))) : 100;
  const wp = widthPct >= 100 ? null : `${widthPct}%`;
  const w = widthMode === "px" ? wpx : wp;
  // Use a table for better email client consistency and avoid extra height differences vs editor.
  if (w) {
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
      <tr>
        <td align="center" style="${styleObjToString({ "font-size": "0", "line-height": "0" })}">
          <table role="presentation" width="${w}" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;width:${w};">
            <tr>
              <td style="${styleObjToString({
                "border-top": `${thickness}px solid ${color}`,
                "font-size": "0",
                "line-height": "0",
                height: "0",
              })}">&nbsp;</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>`;
  }
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
      <tr>
        <td style="${styleObjToString({
          "border-top": `${thickness}px solid ${color}`,
          "font-size": "0",
          "line-height": "0",
          height: "0",
        })}">&nbsp;</td>
      </tr>
    </table>`;
}

function renderSpacerBlock(block) {
  const s = block.settings || {};
  const h = Number(s.height ?? 16);
  return `<div style="height:${h}px;line-height:${h}px;font-size:${h}px">&nbsp;</div>`;
}

function renderBlock(block, doc) {
  switch (block.type) {
    case "text":
      return renderTextBlock(block, doc);
    case "image":
      return renderImageBlock(block, doc);
    case "button":
      return renderButtonBlock(block, doc);
    case "divider":
      return renderDividerBlock(block);
    case "spacer":
      return renderSpacerBlock(block);
    default:
      return "";
  }
}

function renderBlockWithMode(block, doc, mode) {
  switch (block.type) {
    case "text":
      return renderTextBlock(block, doc);
    case "image":
      return renderImageBlock(block, { mode });
    case "button":
      return renderButtonBlock(block, doc);
    case "divider":
      return renderDividerBlock(block);
    case "spacer":
      return renderSpacerBlock(block);
    default:
      return "";
  }
}

function renderRow(row, doc, mode) {
  const rs = row.settings || {};
  const cols = row.columns || [];
  const colCount = Math.max(1, cols.length);
  const rowAlignRaw = String(rs.align || "").toLowerCase();
  const rowAlign = rowAlignRaw === "center" || rowAlignRaw === "right" ? rowAlignRaw : "left";
  const mobileOrientation = rs.mobileOrientation || "stack"; // stack | stack-reverse | keep
  const hasMobileAltLayout = colCount > 1 && mobileOrientation !== "keep";
  const weightsRaw = Array.isArray(rs.columnWeights) ? rs.columnWeights : [];
  const weights = Array.from({ length: colCount }, (_, i) => {
    const w = Number(weightsRaw[i] ?? 1);
    return Number.isFinite(w) && w > 0 ? w : 1;
  });
  const totalW = weights.reduce((a, b) => a + b, 0) || colCount;
  const colWidthPcts = weights.map((w) => Math.max(1, Math.floor((w / totalW) * 100)));
  // ensure sum is 100 by adjusting last column
  const sum = colWidthPcts.reduce((a, b) => a + b, 0);
  colWidthPcts[colWidthPcts.length - 1] = Math.max(1, colWidthPcts[colWidthPcts.length - 1] + (100 - sum));
  const padTop = Number(rs.paddingTop ?? 8);
  const padBottom = Number(rs.paddingBottom ?? 8);
  const padLeftRow = Number(rs.paddingLeft ?? 8);
  const padRightRow = Number(rs.paddingRight ?? 8);

  const wrapperBorder = rs.border || null;
  const wrapperBorderWidthRaw = Number(wrapperBorder?.width ?? 1);
  const wrapperBorderWidth = Number.isFinite(wrapperBorderWidthRaw)
    ? Math.min(20, Math.max(1, Math.round(wrapperBorderWidthRaw)))
    : 1;
  const rowBr = rs.borderRadius ?? 0;
  const rowBrCss = formatBorderRadius(rowBr);
  const rowBgRaw = rs.backgroundColor || null;
  const rowBg = rowBgRaw ? hexToCssColor(rowBgRaw) : null;
  const rowBgImage = backgroundImageUrlFromSettings(rs);
  const wrapperStyle = styleObjToString({
    "background-color": rowBg,
    ...backgroundStylesFromSettings(rs),
    ...borderToSideStyles(wrapperBorder, { width: wrapperBorderWidth, defaultColor: "#e5e7eb" }),
    "border-radius": rowBrCss !== "0px" ? rowBrCss : null,
    overflow: rowBrCss !== "0px" ? "hidden" : null,
    // Nested tables inside a TD add a descender/line-box gap in many clients.
    // Collapse it here and restore font on the inner padding cell.
    "font-size": "0",
    "line-height": "0",
  });

  // Block spacing is controlled per-block via margin settings (rendered as email-safe TD padding)
  const renderBlocksTable = (col, colAlign = "left") => {
    const blocksArr = col.blocks || [];
    let out = "";
    const isInlineItem = (b) => {
      const d = String(b?.settings?.display || "").toLowerCase();
      const isInline = d === "inline-block" || d === "inline";
      return isInline && (b?.type === "image" || b?.type === "text");
    };

    for (let i = 0; i < blocksArr.length; i += 1) {
      const b = blocksArr[i];
      const bs = b?.settings || {};
      const mt = Number.isFinite(Number(bs.marginTop)) ? Number(bs.marginTop) : 8;
      const mr = Number.isFinite(Number(bs.marginRight)) ? Number(bs.marginRight) : 0;
      const mb = Number.isFinite(Number(bs.marginBottom)) ? Number(bs.marginBottom) : 0;
      const ml = Number.isFinite(Number(bs.marginLeft)) ? Number(bs.marginLeft) : 0;

      if (isInlineItem(b)) {
        // Inline items (text/images) need to share the same table cell to truly render inline.
        // If each block is wrapped in its own <tr>, email layout tables will stack them vertically.
        const group = [b];
        let j = i + 1;
        while (j < blocksArr.length && isInlineItem(blocksArr[j])) {
          group.push(blocksArr[j]);
          j += 1;
        }
        i = j - 1;

        // Use column alignment instead of per-block alignment for consistent layout.
        const align = colAlign || "left";
        const cells = group
          .map((blk) => {
            const s = blk?.settings || {};
            const gmt = Number.isFinite(Number(s.marginTop)) ? Number(s.marginTop) : 8;
            const gmr = Number.isFinite(Number(s.marginRight)) ? Number(s.marginRight) : 0;
            const gmb = Number.isFinite(Number(s.marginBottom)) ? Number(s.marginBottom) : 0;
            const gml = Number.isFinite(Number(s.marginLeft)) ? Number(s.marginLeft) : 0;
            const inner =
              blk?.type === "image"
                ? renderImageBlock(blk, { mode, inline: true })
                : renderBlockWithMode(blk, doc, mode);
            return `<td valign="middle" style="${styleObjToString({
              "vertical-align": "middle",
              padding: `${gmt}px ${gmr}px ${gmb}px ${gml}px`,
            })}">${inner}</td>`;
          })
          .join("");

        const groupTable = `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;display:inline-table;">
          <tr>${cells}</tr>
        </table>`;

        out += `<tr><td valign="top" style="${styleObjToString({
          padding: "0",
          "text-align": align,
        })}">${groupTable}</td></tr>`;
      } else {
        // Normal block
        out += `<tr><td valign="top" style="${styleObjToString({
          padding: `${mt}px ${mr}px ${mb}px ${ml}px`,
        })}">${renderBlockWithMode(b, doc, mode)}</td></tr>`;
      }
    }

    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
      ${out}
    </table>`;
  };

  const renderDesktopColumnsTable = () => {
    const gap = Number(rs.columnGap ?? 0);
    const columnsWithGaps = [];
    
    for (let idx = 0; idx < cols.length; idx += 1) {
      if (idx > 0 && gap > 0) {
        // Insert gap column
        columnsWithGaps.push({
          type: "gap",
          width: gap,
        });
      }
      columnsWithGaps.push({
        type: "column",
        col: cols[idx],
        idx,
      });
    }
    
    const columnsInner = columnsWithGaps
      .map((item) => {
        if (item.type === "gap") {
          // Render gap column
          return `<td style="${styleObjToString({
            width: px(item.width),
            'min-width': px(item.width),
            'max-width': px(item.width),
            padding: "0",
            "background-color": "transparent",
            border: "none",
            outline: "none",
          })}"></td>`;
        }
        
        // Render content column
        const col = item.col;
        const cs = col?.settings || {};
        const colBorder = cs.border || null;
        const colBr = cs.borderRadius ?? 0;
        const colBrCss = formatBorderRadius(colBr);
        const colAlignRaw = String(cs.align || "").toLowerCase();
        const colAlign = colAlignRaw === "left" || colAlignRaw === "center" || colAlignRaw === "right" ? colAlignRaw : rowAlign;
        const colVAlign = normalizeVAlign(cs.verticalAlign);
        const cpt = Number(cs.paddingTop ?? 8);
        const cpb = Number(cs.paddingBottom ?? 8);
        const cpl = Number(cs.paddingLeft ?? 8);
        const cpr = Number(cs.paddingRight ?? 8);
        const cBgRaw = cs.backgroundColor || null;
        const cBg = cBgRaw ? hexToCssColor(cBgRaw) : null;
        const blocksHtml = renderBlocksTable(col, colAlign);
        const innerWrapper = `<div style="${styleObjToString({
          width: "100%",
          "box-sizing": "border-box",
        })}">${blocksHtml}</div>`;

        return `<td valign="${colVAlign}" width="${colWidthPcts[item.idx]}%"${htmlBackgroundAttr(backgroundImageUrlFromSettings(cs))} style="${styleObjToString({
          "vertical-align": colVAlign,
          "text-align": colAlign,
          "background-color": cBg,
          ...backgroundStylesFromSettings(cs),
          "padding-top": px(Number.isFinite(cpt) ? cpt : 0),
          "padding-bottom": px(Number.isFinite(cpb) ? cpb : 0),
          "padding-left": px(Number.isFinite(cpl) ? cpl : 0),
          "padding-right": px(Number.isFinite(cpr) ? cpr : 0),
          "border-radius": colBrCss !== "0px" ? colBrCss : null,
          ...outlineToStyles(colBorder, { defaultColor: "#e5e7eb" }),
        })}">${innerWrapper}</td>`;
      })
      .join("");

    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" class="${hasMobileAltLayout ? "kv-ve-desktop-only" : ""}" style="border-spacing:0;">
      <tr>${columnsInner}</tr>
    </table>`;
  };

  const renderMobileStackTable = () => {
    const orderedCols = mobileOrientation === "stack-reverse" ? cols.slice().reverse() : cols;
    const rows = orderedCols
      .map((col, idx) => {
        const cs = col?.settings || {};
        const colBorder = cs.border || null;
        const colBr = cs.borderRadius ?? 0;
        const colBrCss = formatBorderRadius(colBr);
        const colAlignRaw = String(cs.align || "").toLowerCase();
        const colAlign = colAlignRaw === "left" || colAlignRaw === "center" || colAlignRaw === "right" ? colAlignRaw : rowAlign;
        const colVAlign = normalizeVAlign(cs.verticalAlign);
        const cpt = Number(cs.paddingTop ?? 8);
        const cpb = Number(cs.paddingBottom ?? 8);
        const cpl = Number(cs.paddingLeft ?? 8);
        const cpr = Number(cs.paddingRight ?? 8);
        const cBgRaw = cs.backgroundColor || null;
        const cBg = cBgRaw ? hexToCssColor(cBgRaw) : null;
        const blocksHtml = renderBlocksTable(col, colAlign);
        const innerWrapper = `<div style="${styleObjToString({
          width: "100%",
          "box-sizing": "border-box",
        })}">${blocksHtml}</div>`;

        const colRow = `<tr><td valign="${colVAlign}"${htmlBackgroundAttr(backgroundImageUrlFromSettings(cs))} style="${styleObjToString({
          "vertical-align": colVAlign,
          "text-align": colAlign,
          "background-color": cBg,
          ...backgroundStylesFromSettings(cs),
          "padding-top": px(Number.isFinite(cpt) ? cpt : 0),
          "padding-bottom": px(Number.isFinite(cpb) ? cpb : 0),
          "padding-left": px(Number.isFinite(cpl) ? cpl : 0),
          "padding-right": px(Number.isFinite(cpr) ? cpr : 0),
          "border-radius": colBrCss !== "0px" ? colBrCss : null,
          ...outlineToStyles(colBorder, { defaultColor: "#e5e7eb" }),
        })}">${innerWrapper}</td></tr>`;
        return colRow;
      })
      .join("");

    // Hidden by default, shown by media query on mobile.
    const table = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" class="kv-ve-mobile-only" style="border-spacing:0;display:none;max-height:0;overflow:hidden;mso-hide:all;">
      ${rows}
    </table>`;
    // Prevent Outlook desktop (mso) from ever rendering the duplicated mobile layout.
    // Other clients ignore conditional comments and will still rely on the media query swap.
    return `<!--[if !mso]><!-->${table}<!--<![endif]-->`;
  };

  const paddedRow = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">
    <tr>
      <td style="${styleObjToString({
        "padding-top": px(padTop),
        "padding-bottom": px(padBottom),
        "padding-left": px(padLeftRow),
        "padding-right": px(padRightRow),
        "font-size": "16px",
        "line-height": "1.5",
      })}">
        ${renderDesktopColumnsTable()}
        ${hasMobileAltLayout ? renderMobileStackTable() : ""}
      </td>
    </tr>
  </table>`;

  const marginTop = Number(rs.marginTop ?? 0);
  const marginRight = Number(rs.marginRight ?? 0);
  const marginBottom = Number(rs.marginBottom ?? 0);
  const marginLeft = Number(rs.marginLeft ?? 0);
  const safeMargin = (n) => (Number.isFinite(n) && n > 0 ? Math.round(n) : 0);
  const mt = safeMargin(marginTop);
  const mr = safeMargin(marginRight);
  const mb = safeMargin(marginBottom);
  const ml = safeMargin(marginLeft);
  const emailWidth = Number(doc?.body?.emailWidth ?? doc?.body?.width ?? 600) || 600;
  const innerWidth = Math.max(1, Math.round(emailWidth) - ml - mr);
  const hasHMargin = Boolean(ml || mr);

  // Horizontal inset = gutter TDs in the SAME table as other rows.
  // Do not wrap the bordered cell in another table/padded TD — that creates a
  // white ghost gap under the nested table and is what preview was showing.
  // Pin the bordered cell to a pixel width so the inner width:100% table
  // cannot expand and crush the gutters (that is what made margin "stop working").
  const gutterCell = (w) =>
    `<td width="${w}" style="${styleObjToString({
      width: px(w),
      "min-width": px(w),
      "max-width": px(w),
      padding: "0",
      border: "0",
      "font-size": "0",
      "line-height": "0",
      "mso-line-height-rule": "exactly",
    })}">&nbsp;</td>`;
  const hCols = (ml ? 1 : 0) + 1 + (mr ? 1 : 0);
  const spacerRow = (h) =>
    `<tr><td colspan="${hCols}" height="${h}" style="${styleObjToString({
      height: px(h),
      "font-size": "0",
      "line-height": "0",
      "mso-line-height-rule": "exactly",
      padding: "0",
      border: "0",
    })}">&nbsp;</td></tr>`;

  const boxedStyle = styleObjToString({
    ...(hasHMargin ? { width: px(innerWidth), "max-width": px(innerWidth) } : {}),
  });
  const boxedTdStyle = [boxedStyle, wrapperStyle].filter(Boolean).join(";");
  const contentRow = hasHMargin
    ? `<tr>${ml ? gutterCell(ml) : ""}<td width="${innerWidth}"${htmlBackgroundAttr(rowBgImage)} style="${boxedTdStyle}">${paddedRow}</td>${mr ? gutterCell(mr) : ""}</tr>`
    : `<tr><td colspan="3"${htmlBackgroundAttr(rowBgImage)} style="${wrapperStyle}">${paddedRow}</td></tr>`;

  return `${mt ? spacerRow(mt) : ""}${contentRow}${mb ? spacerRow(mb) : ""}`;
}

function renderContainer(container, doc, mode) {
  const body = doc?.body || {};
  const width = Number(body.emailWidth ?? body.width ?? 600);

  const s = container?.settings || {};
  const stripeBgMode = String(s.backgroundMode || "").toLowerCase() === "image" ? "image" : "color";
  const stripeBgImg = s.backgroundImageUrl || null;
  const isStripeBgImage = stripeBgMode === "image" && stripeBgImg;
  const stripeBgRaw = s.backgroundColor || null;
  // When using background image, use transparent or fallback color
  const stripeBg = isStripeBgImage ? (stripeBgRaw ? hexToCssColor(stripeBgRaw) : "transparent") : (stripeBgRaw ? hexToCssColor(stripeBgRaw) : null);
  const contentBgRaw = s.contentBackgroundColor || body.contentBackgroundColor || "#ffffff";
  const contentBg = hexToCssColor(contentBgRaw);
  
  // Content background image support (from container settings or body defaults)
  const contentBgSettings = {
    backgroundMode: s.contentBackgroundMode || body.contentBackgroundMode,
    backgroundImageUrl: s.contentBackgroundImageUrl || body.contentBackgroundImageUrl,
    backgroundSizeMode: s.contentBackgroundSizeMode || body.contentBackgroundSizeMode,
    backgroundSizeUnit: s.contentBackgroundSizeUnit || body.contentBackgroundSizeUnit,
    backgroundSizePx: s.contentBackgroundSizePx || body.contentBackgroundSizePx,
    backgroundSizePct: s.contentBackgroundSizePct || body.contentBackgroundSizePct,
    backgroundRepeat: s.contentBackgroundRepeat || body.contentBackgroundRepeat,
    backgroundPosition: s.contentBackgroundPosition || body.contentBackgroundPosition,
  };
  const contentBgStyles = backgroundStylesFromSettings(contentBgSettings);
  const contentBgImageUrl = backgroundImageUrlFromSettings(contentBgSettings);
  const contentBgSize = contentBgStyles["background-size"];
  const contentBgPosition = contentBgStyles["background-position"];
  const contentBgRepeat = contentBgStyles["background-repeat"];
  
  const contentBr = Number(s.contentBorderRadius ?? 0);
  const contentBorderStyle = String(s.contentBorderStyle || "none").toLowerCase();
  const contentBorderColorRaw = s.contentBorderColor || "#e5e7eb";
  const contentBorderColor = hexToCssColor(contentBorderColorRaw);
  const contentBorderWidth = Number(s.contentBorderWidth ?? 0);
  const borderEnabled = contentBorderStyle !== "none" && Number.isFinite(contentBorderWidth) && contentBorderWidth > 0;
  const contentBorder = borderEnabled
    ? {
        enabled: true,
        style: contentBorderStyle,
        color: contentBorderColor,
        sides: s.contentBorderSides || { top: true, right: true, bottom: true, left: true },
      }
    : { enabled: false };

  const pt = Number(s.paddingTop ?? 0);
  const pr = Number(s.paddingRight ?? 0);
  const pb = Number(s.paddingBottom ?? 0);
  const pl = Number(s.paddingLeft ?? 0);
  const safe = (n) => (Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0);
  const pad = { pt: safe(pt), pr: safe(pr), pb: safe(pb), pl: safe(pl) };

  const rowsHtml = (container?.rows || []).map((row) => renderRow(row, doc, mode)).join("");

  const containerId = container.id;
  // Full-width stripe: background on outer TD, centered fixed-width content inside.
  return `<!-- start-container:${containerId} -->
  <tr>
    <td width="100%"${htmlBackgroundAttr(backgroundImageUrlFromSettings(s))} style="${styleObjToString({
      width: "100%",
      "background-color": stripeBg,
      ...backgroundStylesFromSettings(s),
    })}">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;">
        <tr>
          <td align="center" style="${styleObjToString({
            "padding-top": px(pad.pt),
            "padding-bottom": px(pad.pb),
            "padding-left": px(pad.pl),
            "padding-right": px(pad.pr),
          })}">
            <table role="presentation" width="${width}" cellpadding="0" cellspacing="0" border="0"${htmlBackgroundAttr(contentBgImageUrl)} style="${styleObjToString({
              width: "100%",
              "max-width": `${width}px`,
              "border-collapse": "collapse",
              "background-color": contentBg,
              "background-size": contentBgSize,
              "background-position": contentBgPosition,
              "background-repeat": contentBgRepeat,
              "border-radius": Number.isFinite(contentBr) && contentBr > 0 ? `${Math.round(contentBr)}px` : null,
              overflow: Number.isFinite(contentBr) && contentBr > 0 ? "hidden" : null,
              ...borderToSideStyles(contentBorder, { width: Number.isFinite(contentBorderWidth) && contentBorderWidth > 0 ? Math.round(contentBorderWidth) : 1, defaultColor: contentBorderColor }),
            })}">
              ${rowsHtml}
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
<!-- end-container:${containerId} -->`;
}

export function renderEmailHtml(doc, options = {}) {
  const mode = options.mode || "export"; // export | preview
  const body = doc?.body || {};
  const width = Number(body.emailWidth ?? body.width ?? 600);
  const borderRadius = body.borderRadius ?? 0;
  const borderRadiusCss = formatBorderRadius(borderRadius);
  const topSpacing = Number(body.topSpacing ?? 16);
  // Vertical spacing around the whole email. Historically this accidentally used `mobilePadding` (which is horizontal).
  // Keep backward-compat by defaulting to `topSpacing` when `bottomSpacing` isn't present.
  const bottomSpacing = Number(body.bottomSpacing ?? body.topSpacing ?? 16);
  const bodyBgRaw = body.backgroundColor || "#ffffff";
  const bodyBg = hexToCssColor(bodyBgRaw);
  const bodyContentBgRaw = body.contentBackgroundColor || "#ffffff";
  const bodyContentBg = hexToCssColor(bodyContentBgRaw);
  
  // Body background image support
  const bodyBgStyles = backgroundStylesFromSettings(body);
  const bodyBgImageUrl = backgroundImageUrlFromSettings(body);
  const bodyBgSize = bodyBgStyles["background-size"];
  const bodyBgPosition = bodyBgStyles["background-position"];
  const bodyBgRepeat = bodyBgStyles["background-repeat"];
  
  // Build body background style string (no url() — Gmail strips the whole style)
  const bodyBgStyleParts = [`background-color:${bodyBg}`];
  if (bodyBgImageUrl) {
    if (bodyBgSize) bodyBgStyleParts.push(`background-size:${bodyBgSize}`);
    if (bodyBgPosition) bodyBgStyleParts.push(`background-position:${bodyBgPosition}`);
    if (bodyBgRepeat) bodyBgStyleParts.push(`background-repeat:${bodyBgRepeat}`);
  }
  const bodyBgStyle = bodyBgStyleParts.join(";");

  const containers = Array.isArray(doc?.containers) ? doc.containers : null;
  const rowsHtml = containers && containers.length
    ? containers.map((c) => renderContainer(c, doc, mode)).join("")
    : (doc?.rows || []).map((row) => renderRow(row, doc, mode)).join("");

  const baseFontSize = 16;
  const baseLineHeight = 1.5;
  const shadowCss =
    mode === "preview"
      ? "box-shadow: 0 0 0 1px rgba(16, 24, 40, 0.06), 0 10px 30px rgba(16,24,40,0.08);"
      : "";

  const isStripeMode = Boolean(containers && containers.length);
  // In stripe/containers mode, containers are meant to control their own spacing.
  // So we default the global wrapper vertical padding to 0 to avoid an always-on 16px gap.
  const outerPadTop = isStripeMode ? 0 : topSpacing;
  const outerPadBottom = isStripeMode ? 0 : bottomSpacing;

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Email</title>
    <style>
      /* Reset/normalize so preview/export isn't affected by host app styles */
      html, body { margin: 0; padding: 0; }
      * { box-sizing: border-box; }
      img { max-width: 100%; height: auto; display: block; border: 0; outline: none; }
      table { border-collapse: collapse; }
      h1,h2,h3,h4,h5,h6,p,blockquote,pre,ul,ol,li { margin: 0; padding: 0; }
      ul,ol { padding-left: 18px; }
      a { color: ${body.linkColor || "#0ea5e9"}; text-decoration: underline; }
      body { font-family: ${body.fontFamily || "Helvetica, Arial, sans-serif"}; font-size: ${baseFontSize}px; line-height: ${baseLineHeight}; color: ${body.textColor || "#111827"}; text-transform: none; letter-spacing: normal; font-weight: 400; }
      h1 { font-size: 32px; line-height: 1.2; font-weight: 700; text-transform: none; letter-spacing: normal; }
      h2 { font-size: 24px; line-height: 1.2; font-weight: 700; text-transform: none; letter-spacing: normal; }
      p, li { font-size: ${baseFontSize}px; line-height: ${baseLineHeight}; }

      /* Email responsiveness (best-effort across modern clients).
         Outlook desktop will ignore media queries (keeps desktop columns). */
      @media only screen and (max-width: 600px) {
        .kv-ve-desktop-only {
          display: none !important;
          max-height: 0 !important;
          overflow: hidden !important;
        }
        .kv-ve-mobile-only {
          display: table !important;
          width: 100% !important;
          max-height: none !important;
          overflow: visible !important;
        }
      }
    </style>
  </head>
  <body${htmlBackgroundAttr(bodyBgImageUrl)} style="margin:0;padding:0;${bodyBgStyle}">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"${htmlBackgroundAttr(bodyBgImageUrl)} style="${bodyBgStyle}">
      ${
        isStripeMode
          ? `<tr><td style="padding:${outerPadTop}px 0 ${outerPadBottom}px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;">
                ${rowsHtml}
              </table>
            </td></tr>`
          : `<tr>
              <td align="center" style="padding:${outerPadTop}px 12px ${outerPadBottom}px;">
                <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="${width}" class="kv-ve-container" style="width:100%;max-width:${width}px;border-collapse:separate;border-spacing:0;border-radius:${borderRadiusCss};background-color:${bodyContentBg};overflow:hidden;${shadowCss}">
                  <tr>
                    <td style="border-radius:${borderRadiusCss};overflow:hidden;">
                      <table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%" style="width:100%;border-collapse:collapse;background-color:${bodyContentBg}">
                        ${rowsHtml}
                      </table>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>`
      }
    </table>
  </body>
</html>`;
}


